import type { ReactNode } from 'react';
import { siteConfig } from '@sighthost/config';
import { BrandLogo } from '@sighthost/ui';
import { AuthLeftPanel } from './AuthLeftPanel';
import { TrustBlock } from './TrustBlock';
import { PlanRecapCard, loadWizardPlan } from './PlanRecapCard';

/**
 * AuthLayout — two-panel wrapper for /sign-up + /sign-in.
 *
 * Spec: _bmad-output/AUTH_AND_TRUST_BRD_2026-05-17.md §5.1, ATL-7, ATL-8, ATL-9.
 *
 * Variants (resolved by the consuming page — see §5.3):
 *   wizard-return     — sessionStorage has pending_hosting_order_v1
 *   marketing-direct  — direct sign-up / sign-in (no wizard, no recovery)
 *   password-recovery — ?recovery=1 / PASSWORD_RECOVERY token flow
 *   invite-claim      — ?invite=1 token flow
 *   staff             — /admin sign-in path OR known staff user
 *
 * Layout:
 *   Desktop (md+): 2-column grid. Left = AuthLeftPanel (context). Right = form.
 *   Mobile (<md):  Single column. Mobile context strip above form. Trust block
 *                  below form. Trust block + Hindsight copy suppressed for the
 *                  staff variant per ATL-7.
 */

export type AuthVariant =
  | 'wizard-return'
  | 'marketing-direct'
  | 'password-recovery'
  | 'invite-claim'
  | 'lead-claim'   // Phase 11 SP-21 — converted lead landing from `?lead_claim=1`
  | 'staff';

interface AuthLayoutProps {
  variant: AuthVariant;
  children: ReactNode;
}

export function AuthLayout({ variant, children }: AuthLayoutProps) {
  const isStaff = variant === 'staff';
  const wizardPlan = variant === 'wizard-return' ? loadWizardPlan() : null;

  return (
    <main className="min-h-screen md:grid md:grid-cols-2">
      {/* ── Desktop left panel (md+) ───────────────────────────────── */}
      <aside
        className="hidden bg-gradient-to-br from-card via-card to-primary/5 md:flex md:flex-col md:justify-between md:px-12 md:pb-10 md:pt-16"
        role="complementary"
        aria-label="SightHost context and trust"
      >
        <AuthLeftPanel variant={variant} wizardPlan={wizardPlan} />
      </aside>

      {/* ── Right form panel (always rendered) ─────────────────────── */}
      <section
        className="flex flex-col items-center justify-center bg-background px-4 pb-10 pt-14 md:pt-10"
        role="main"
      >
        <div className="w-full max-w-md">
          {/* Mobile-only logo — desktop has it in the left panel. */}
          <div className="mb-6 flex justify-center md:hidden">
            <BrandLogo tone="auto" className="h-14" loading="eager" />
          </div>

          {/* Mobile-only context strip — only meaningful for wizard-return.
              Other variants render nothing on mobile (the form headline is
              context enough). */}
          {variant === 'wizard-return' && wizardPlan && (
            <div className="mb-6 rounded-md border border-border bg-card px-4 py-3 text-sm text-ink md:hidden">
              <span className="text-muted-foreground">Continuing your order:</span>{' '}
              <strong>{wizardPlan.name}</strong> — R {wizardPlan.priceZar}/mo
            </div>
          )}

          {children}

          {/* Mobile trust block (suppressed for staff per ATL-7) */}
          {!isStaff && (
            <div className="mt-8 md:hidden">
              <TrustBlock variant="compact" />
            </div>
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Trouble signing in? Email{' '}
            <a
              href={`mailto:${siteConfig.supportEmail}`}
              className="text-primary-ink hover:underline"
            >
              {siteConfig.supportEmail}
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}

/**
 * Render the same plan-recap card the desktop AuthLeftPanel uses. Exported for
 * pages that want to anchor the wizard state above their own form before
 * adopting AuthLayout end-to-end.
 */
export { PlanRecapCard, loadWizardPlan };
